import * as React from 'react';
import { connect, Dispatch } from 'react-redux';
import { Settings as SliderSettings } from 'react-slick';
import { StoreState } from '../../reducers';
import { GalleryInfosState } from '../Gallery/reducer';
import TheaterHeader from './Header';
import TheaterFooter from './Footer';
import { DriveListState } from '../DriveItemList/reducer';
import { fetchDriveItem, setReadStatus } from '../DriveItemList/actions';
import { TheaterOverlay, TheaterContainer, TheaterItem } from './styles';
import TheaterContent from './Content';
import { hashHistory } from 'react-router';
import { Api } from '../../typings/api';
import Slider from './Slider';
import { DriveItemListOrigin } from '../DriveItemListPage/constants';
import RoutesUtils from '../../utils/routes';
import StatsUtils from '../../utils/statistics';

export interface TheaterProps {
	sliderKey: string;
	dispatch: Dispatch<StoreState>;
	galleryInfos: GalleryInfosState;
	driveList: DriveListState;
	origin: DriveItemListOrigin;
	driveItemId: number;
	driveItemIds: number[];
	isLoading: boolean;
	folderId?: number;
	leavePage: () => void;
}

export class Theater extends React.Component<TheaterProps, {}> {

	newItemId: number;
	sliderIndex: number;

	constructor(props: TheaterProps) {
		super(props);
		this.newItemId = -1;
		this.beforeChangeSliderItem = this.beforeChangeSliderItem.bind(this);
		this.afterChangeSliderItem = this.afterChangeSliderItem.bind(this);
	}

	componentDidMount() {
		this.initializeView(this.props);
	}

	componentWillReceiveProps(nextProps: TheaterProps) {
		this.initializeView(nextProps);
	}

	render() {
		if (this.props.driveItemIds.length > 0) {
			const { driveList, driveItemId, driveItemIds, galleryInfos, leavePage } = this.props;
			const { resolutions } = driveList;
			const { driveItem } = this;
			const driveItemIndex = driveItemIds.indexOf(driveItemId);
			const sliderSettings = this.getSliderSettings(driveItemIndex);
			const sliderChildren = this.renderTheaterContents(galleryInfos, resolutions);

			let sliderKey = this.props.sliderKey;

			if (driveItem) {
				sliderKey = `${this.props.sliderKey}-${driveItem.id}`;
				this.playMediaOnEnter(driveItemIndex);
			}

			return (
				<TheaterOverlay>
					{driveItem ?
						<TheaterContainer>
							<TheaterHeader
								leavePage={leavePage}
								sliderIndex={(this.sliderIndex || driveItemIndex) + 1}
								sliderLength={driveItemIds.length}
							/>
							<Slider
								shouldReRender={this.shouldSliderComponentReRender(driveItemIndex)}
								sliderKey={sliderKey}
								sliderSettings={sliderSettings}
							>
								{sliderChildren}
							</Slider>
							<TheaterFooter
								driveItem={driveItem}
							/>
						</TheaterContainer>
						:
						null
					}
				</TheaterOverlay>
			);
		}

		return null;
	}

	private initializeView(props: TheaterProps): void {
		const { driveItemId, driveItemIds, isLoading, origin } = props;
		if (driveItemIds.length > 0) {
			if (!this.sliderIndex || driveItemIds[this.sliderIndex] !== driveItemId) {
				this.sliderIndex = driveItemIds.indexOf(driveItemId);
			}

			const { dispatch, folderId } = props;
			if (this.props.driveItemIds.length > props.driveItemIds.length) {
				// When a drive item has been removed from the drive item list
				const oldSliderIndex = this.props.driveItemIds.indexOf(this.props.driveItemId);
				const newSliderIndex = oldSliderIndex < props.driveItemIds.length ? oldSliderIndex : oldSliderIndex - 1;
				hashHistory.replace(RoutesUtils.getPathTheater(origin, null, props.driveItemIds[newSliderIndex]));
			}

			dispatch(fetchDriveItem(driveItemId, folderId));
		} else if (!isLoading) {
			hashHistory.replace(RoutesUtils.getPathDriveItemListPage(origin, null));
		}
	}

	private getSliderSettings(initialSlide: number): SliderSettings {
		return {
			dots: false,
			infinite: false,
			speed: 500,
			lazyLoad: 'ondemand',
			initialSlide,
			beforeChange: this.beforeChangeSliderItem,
			afterChange: this.afterChangeSliderItem,
		};
	}

	private beforeChangeSliderItem(oldSliderIndex: number, newSliderIndex: number): void {
		this.pauseMediaOnLeave(oldSliderIndex);
	}

	private pauseMediaOnLeave(sliderIndex: number): void {
		this.toggleSliderMedia(sliderIndex, false);
	}

	private toggleSliderMedia(sliderIndex: number, shouldPlay: boolean) {
		// TODO : Find a way to do it without using document.querySelector or React.findDOMNode
		const driveItem = this.driveItemsToDisplay[sliderIndex];

		if (driveItem && (driveItem.type === Api.DriveItemType.Movie || driveItem.type === Api.DriveItemType.Sound)) {
			const mediaElementName = driveItem.type === Api.DriveItemType.Movie ? 'video' : 'audio';
			const mediaElementSelector = `.slick-slide[data-index='${sliderIndex}'] ${mediaElementName}`;
			const media = document.querySelector(mediaElementSelector) as HTMLMediaElement;
			if (media) {
				if (shouldPlay) {
					media.play();
				} else {
					media.pause();
				}
			}
		}
	}

	private afterChangeSliderItem(newSliderIndex: number): void {
		const { driveItemIds, dispatch, folderId } = this.props;
		this.sliderIndex = newSliderIndex;
		this.updateHashHistoryBySliderIndex(newSliderIndex);
		dispatch(fetchDriveItem(driveItemIds[newSliderIndex], folderId))
			.then(response => {
				if (Object.keys(response).length > 0) { // handle case where response is {}
					const driveItem = response as Api.DriveItem;
					const viewableDriveItemTypes = [
						Api.DriveItemType.Image,
						Api.DriveItemType.Movie,
						Api.DriveItemType.Sound,
					];

					if (viewableDriveItemTypes.includes(driveItem.type)) {
						dispatch(setReadStatus(driveItem.id));
						StatsUtils.trackView(driveItem);
					}
				}
			});
	}

	private updateHashHistoryBySliderIndex(sliderIndex: number): void {
		const { driveItemId } = this.props;
		const currentHash = hashHistory.getCurrentLocation().pathname;

		this.newItemId = this.props.driveItemIds[sliderIndex];

		const newHash = currentHash.replace(`/theater/${driveItemId}`, `/theater/${this.newItemId}`);
		hashHistory.replace(newHash);
	}

	private playMediaOnEnter(sliderIndex: number): void {
		this.toggleSliderMedia(sliderIndex, true);
	}

	private shouldSliderComponentReRender(driveItemIndex: number): boolean {
		return this.isHashChangedManually(driveItemIndex) || !this.isDriveItemAlreadyLoaded();
	}

	private isHashChangedManually(driveItemIndex: number) {
		return this.props.driveItemIds[driveItemIndex] !== this.newItemId;
	}

	private isDriveItemAlreadyLoaded(): boolean {
		const { driveItemId, driveItemIds } = this.props;

		if (driveItemIds.length > 0) {
			const driveItemIndex = driveItemIds.indexOf(driveItemId);
			const { driveItemsToDisplay } = this;

			return !!driveItemsToDisplay[driveItemIndex];
		}
		return false;
	}

	private renderTheaterContents(galleryInfos: GalleryInfosState, resolutions: { [k: string]: Api.ResolutionGroup }) {
		return this.driveItemsToDisplay.map((sliderItem, index) => {
			return (
				<TheaterItem key={index}>
					<TheaterContent
						driveItem={sliderItem}
						galleryInfos={galleryInfos}
						resolutions={resolutions}
					/>
				</TheaterItem>
			);
		});
	}

	private get driveItemsToDisplay(): Array<Api.DriveItem | undefined> {
		return this.props.driveItemIds.map(driveItemId => this.getDriveItem(driveItemId));
	}

	private get driveItem(): Api.DriveItem | undefined {
		return this.getDriveItem(this.props.driveItemId);
	}

	private getDriveItem(driveItemId: number): Api.DriveItem | undefined {
		const driveItemsStore = this.props.driveList.driveItems;
		return driveItemsStore[driveItemId];
	}
}


const mapStateToProps = (state: StoreState) => {
	const { galleryInfos, driveList } = state;
	return { galleryInfos, driveList };
};

export default connect(mapStateToProps)(Theater);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Theater/index.tsx